import { zodResolver } from '@hookform/resolvers/zod';
import { MemoClassification } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  FileUploader,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  SimpleSelect,
  Textarea,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { useQuery } from '@tanstack/react-query';
import { LockIcon, PinIcon } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useEventQuery } from '../../../../../hooks/react-query/useEventsQuery';
import { isVideoFile, shouldUseMultipartUpload } from '../../../../../hooks/react-query/useS3';
import { useTimesheetDepartmentQuery } from '../../../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useFileValidation } from '../../../../../hooks/useFileValidation';
import {
  FILE_UPLOAD_ACCEPT,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES,
} from '../../../../../tools/fileUploadUtils';
import { MemoClientService } from '../../../../../tools/helpers';
import { type ZodObjectWithModelKeys } from '../../../../../tools/typeguargs';
import { AudioNoteRow, AudioRecorder } from './AudioRecorder';
import { useSubmitJobNote } from './useSubmitJobNote';

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

type NoteFormFields = {
  classificationId: number;
  description: string;
  files: File[];
  isPinned: boolean;
  isInternal: boolean;
};

const noteFormSchemaObject = z.object({
  classificationId: z.number(),
  description: z.string().max(5000, 'Description is too long (max 5000 characters)'),
  files: z.array(z.instanceof(File)),
  isPinned: z.boolean(),
  isInternal: z.boolean(),
}) satisfies ZodObjectWithModelKeys<NoteFormFields>;

const noteFormSchema = noteFormSchemaObject.refine(() => true, {
  message: 'Either description, file upload or audio recording is required',
  path: ['description'],
});

type NoteFormSchemaType = z.infer<typeof noteFormSchema>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  onClose?: () => void;
  onSuccess: () => void;
  /** Callback to notify parent when processing (uploads) are occurring */
  onProcessingChange?: (loading: boolean) => void;
  loggedUserId: number;
  jobId?: number;
  showCancelButton?: boolean;
  diagnosisId?: number;
  defaultClassificationId?: number;
  /** Optional array of allowed category IDs. If provided, only these categories will be shown. */
  allowedCategoryIds?: number[];
}

export const NoteForm: FC<Props> = ({
  onClose,
  onSuccess,
  onProcessingChange,
  loggedUserId,
  jobId,
  showCancelButton = true,
  diagnosisId,
  defaultClassificationId = 1,
  allowedCategoryIds,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | undefined>(undefined);
  const [resetKey, setResetKey] = useState(0);

  const memoClassificationQuery = useQuery({
    queryKey: ['memoClassifications'],
    queryFn: async () => {
      const req = MemoClassification.create();
      const response = await MemoClientService.BatchGetMemoCategory(req);
      return response;
    },
  });

  const { data: event } = useEventQuery({
    filter: { id: jobId },
    enabled: !!jobId,
  });

  const { data: department, isLoading: isDepartmentLoading } = useTimesheetDepartmentQuery({
    filter: { id: event?.departmentId || 0 },
    enabled: !!event?.departmentId,
  });

  const categoryOptions = useMemo(() => {
    const allCategories = memoClassificationQuery.data?.results || [];
    const filteredCategories = allowedCategoryIds
      ? allCategories.filter((classification) => allowedCategoryIds.includes(classification.id))
      : allCategories;

    return filteredCategories.map((classification) => ({
      value: classification.id.toString(),
      label: classification.categoryDescription,
    }));
  }, [memoClassificationQuery.data?.results, allowedCategoryIds]);

  const form = useForm<NoteFormSchemaType>({
    resolver: zodResolver(noteFormSchema),
    mode: 'onBlur',
    defaultValues: {
      classificationId: defaultClassificationId,
      description: '',
      files: [],
      isPinned: false,
      isInternal: true,
    },
  });

  const files = useWatch({ control: form.control, name: 'files' });
  const description = useWatch({ control: form.control, name: 'description' });

  // Shared real-time file validation
  const { validationError } = useFileValidation(form, 'files', files);

  const hasContent = useCallback(() => {
    return (description && description.trim() !== '') || (files && files.length > 0) || audioBlob;
  }, [description, files, audioBlob]);

  useEffect(() => {
    if (hasContent()) {
      form.clearErrors('description');
    }
  }, [form, hasContent]);

  const validateForm = () => {
    if (hasContent()) {
      form.clearErrors('description');
      return true;
    } else {
      form.setError('description', {
        type: 'manual',
        message: 'Either description, file upload or audio recording is required',
      });
      return false;
    }
  };

  const {
    submitNote,
    resetProgress,
    uploadProgress,
    isPending,
    isLargeFileUploading,
    isUploadingLargeVideos,
    isUpdatingMemo,
  } = useSubmitJobNote();

  // Client-side image prep runs before createMemo, so isPending only flips
  // partway through submission. This flag covers the full lifecycle so the
  // submit button + file X buttons disable as soon as the user clicks Submit.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setResetKey((prev) => prev + 1);
    form.reset({
      classificationId: defaultClassificationId,
      description: '',
      files: [],
      isPinned: false,
      isInternal: true,
    });
    setAudioBlob(null);
    setAudioDuration(undefined);
    resetProgress();
    setTimeout(() => validateForm(), 0);
  };

  const handleSubmit: SubmitHandler<NoteFormSchemaType> = async (data) => {
    if (isRecording) return;
    if (!validateForm()) return;
    if (isSubmitting) return;

    setIsSubmitting(true);
    onProcessingChange?.(true);

    try {
      await submitNote({
        description: data.description,
        classificationId: data.classificationId,
        files: data.files,
        audioBlob,
        isPinned: data.isPinned,
        isInternal: data.isInternal,
        jobId,
        loggedUserId,
        diagnosisId,
      });

      resetForm();
      onSuccess();
      if (onClose) onClose();
      setIsSubmitting(false);
      onProcessingChange?.(false);
    } catch (error) {
      const errorContext = {
        formData: {
          description: data.description,
          classificationId: data.classificationId,
          fileCount: data.files.length,
          hasAudio: !!audioBlob,
        },
        files: data.files.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
          isVideo: isVideoFile(f),
          shouldUseMultipart: shouldUseMultipartUpload(f),
        })),
        jobId,
        userId: loggedUserId,
      };

      console.error('Note submission failed:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorContext,
        originalError: error,
      });

      toast({
        title: 'Note not saved',
        description:
          'The note could not be uploaded. Please try again — if you attached large images, try fewer at a time.',
        variant: 'destructive',
      });

      setIsSubmitting(false);
      onProcessingChange?.(false);
    }
  };

  const isSaveButtonDisabled =
    isRecording ||
    isSubmitting ||
    isPending ||
    isUploadingLargeVideos ||
    isUpdatingMemo ||
    !!validationError;

  const uploadProgressValues = Object.values(uploadProgress);
  const overallProgress =
    uploadProgressValues.length > 0
      ? Math.round(
          uploadProgressValues.reduce((sum, progress) => sum + progress, 0) /
            uploadProgressValues.length,
        )
      : 0;

  const getSubmitButtonText = () => {
    if (isPending) return 'Submitting...';
    if ((isUploadingLargeVideos || isUpdatingMemo) && uploadProgressValues.length > 0) {
      return `Uploading Videos... ${overallProgress}%`;
    }
    if (isSubmitting) return 'Preparing...';
    return 'Submit';
  };

  const isProcessingState =
    isSubmitting || isPending || isUploadingLargeVideos || isUpdatingMemo || isLargeFileUploading;

  return (
    <FormContext {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-5 rounded-lg bg-white"
        onChange={validateForm}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="classificationId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <FormMessage />
                <FormControl>
                  <SimpleSelect
                    values={categoryOptions}
                    selectedValue={field.value ? field.value.toString() : '1'}
                    onChange={(value) => field.onChange(Number(value))}
                    disabled={isProcessingState}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {jobId && (
            <div>
              <label className="text-sm font-medium">Department</label>
              <p className="text-sm text-gray-700">
                {isDepartmentLoading
                  ? 'Loading...'
                  : department?.value || department?.description || 'N/A'}
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-6">
          <FormField
            control={form.control}
            name="isPinned"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isProcessingState}
                  />
                </FormControl>
                <FormLabel className="flex cursor-pointer items-center gap-1 font-normal">
                  <PinIcon className="h-4 w-4" />
                  Pin this note
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isInternal"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isProcessingState}
                  />
                </FormControl>
                <FormLabel className="flex cursor-pointer items-center gap-1 font-normal">
                  <LockIcon className="h-4 w-4" />
                  Internal only
                </FormLabel>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  {...field}
                  rows={4}
                  maxLength={5000}
                  placeholder="Enter note here..."
                  className="resize-y rounded-md border border-gray-200 bg-white"
                />
              </FormControl>
              <FormMessage />
              {(files?.length > 0 || audioBlob) && (
                <p className="text-xs text-gray-500">
                  Description is optional when files or audio are attached
                </p>
              )}
            </FormItem>
          )}
        />

        {/* Record Audio and Upload Media are peers here — no wrapper, so both keep the same button box */}
        <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <FormControl>
              <AudioRecorder
                key={`audio-recorder-${resetKey}`}
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                audioBlob={audioBlob}
                setAudioBlob={setAudioBlob}
                onRecordingComplete={setAudioDuration}
                disabled={isProcessingState}
              />
            </FormControl>
          </div>

          <div className="overflow-hidden rounded-md">
            <FormField
              control={form.control}
              name="files"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FileUploader
                      key={`file-uploader-${resetKey}`}
                      value={field.value}
                      onValueChange={field.onChange}
                      maxFiles={MAX_FILES}
                      multiple
                      noDrag={true}
                      maxSize={MAX_FILE_SIZE_BYTES}
                      disabled={isProcessingState}
                      accept={FILE_UPLOAD_ACCEPT}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {audioBlob && (
          <AudioNoteRow
            audioBlob={audioBlob}
            durationSeconds={audioDuration}
            onClear={() => {
              setAudioBlob(null);
              setAudioDuration(undefined);
            }}
            readOnly={isProcessingState}
          />
        )}
        <p className="text-xs text-gray-500">Maximum {MAX_FILES} images per upload</p>

        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row">
          {showCancelButton && onClose && (
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground min-h-[44px] w-full sm:flex-1"
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
          {isRecording ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="w-full sm:flex-1">
                    <Button type="button" disabled={true} className="min-h-[44px] w-full">
                      Submit
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Please finish recording before saving</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              type="submit"
              disabled={isSaveButtonDisabled}
              className="min-h-[44px] w-full sm:flex-1"
            >
              {getSubmitButtonText()}
            </Button>
          )}
        </div>
      </form>
    </FormContext>
  );
};
